const API_BASE_URL = (process.env.REACT_APP_BLOG_API_BASE_URL || '').replace(/\/$/, '');
const LOCAL_STORAGE_KEY = 'my-website-blog-posts-v1';

function generateId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function createSeedPosts() {
  return [
    {
      id: generateId(),
      title: 'Welcome to the new blog',
      content:
        'This blog is now wired into Cognito roles. Root_Users members can publish posts, while Default users can join the conversation with comments and reactions.',
      authorName: 'Derek Downs',
      createdAt: new Date().toISOString(),
      comments: [
        {
          id: generateId(),
          body: 'This seed post is here so the page has real structure before the AWS API is connected.',
          authorName: 'Site Bot',
          createdAt: new Date().toISOString(),
        },
      ],
      reactionUsers: {
        likes: [],
        dislikes: [],
      },
    },
  ];
}

function normalizePost(post) {
  return {
    ...post,
    comments: Array.isArray(post.comments) ? post.comments : [],
    reactionUsers: {
      likes: Array.isArray(post.reactionUsers?.likes) ? post.reactionUsers.likes : [],
      dislikes: Array.isArray(post.reactionUsers?.dislikes) ? post.reactionUsers.dislikes : [],
    },
  };
}

function sortPosts(posts) {
  return [...posts].sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime());
}

function readLocalPosts() {
  if (typeof window === 'undefined') {
    return createSeedPosts();
  }

  const stored = window.localStorage.getItem(LOCAL_STORAGE_KEY);
  if (!stored) {
    const seededPosts = createSeedPosts();
    window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(seededPosts));
    return seededPosts;
  }

  try {
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed.map(normalizePost) : createSeedPosts();
  } catch (error) {
    return createSeedPosts();
  }
}

function writeLocalPosts(posts) {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(posts));
}

async function apiRequest(path, { method = 'GET', body, token } = {}) {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    let errorMessage = 'The blog request failed.';

    try {
      const errorPayload = await response.json();
      errorMessage = errorPayload?.message || errorPayload?.error || errorMessage;
    } catch (error) {
      errorMessage = await response.text() || errorMessage;
    }

    throw new Error(errorMessage);
  }

  return response.status === 204 ? null : response.json();
}

function usesRemoteApi() {
  return Boolean(API_BASE_URL);
}

export function getBlogStorageMode() {
  return usesRemoteApi() ? 'api' : 'local';
}

export async function listBlogPosts({ token } = {}) {
  if (usesRemoteApi()) {
    const payload = await apiRequest('/posts', { token });
    const posts = Array.isArray(payload?.posts) ? payload.posts : Array.isArray(payload) ? payload : [];
    return sortPosts(posts.map(normalizePost));
  }

  return sortPosts(readLocalPosts());
}

export async function createBlogPost({ title, content, authorName, authorPicture, tags, token }) {
  if (usesRemoteApi()) {
    return apiRequest('/posts', {
      method: 'POST',
      token,
      body: { title, content, authorName, authorPicture, tags },
    });
  }

  const newPost = normalizePost({
    id: generateId(),
    title,
    content,
    authorName,
    authorPicture: authorPicture || null,
    tags: tags || '',
    createdAt: new Date().toISOString(),
    comments: [],
    reactionUsers: { likes: [], dislikes: [] },
  });

  const posts = sortPosts([newPost, ...readLocalPosts()]);
  writeLocalPosts(posts);
  return newPost;
}

export async function addBlogComment(postId, { body, authorName, token }) {
  if (usesRemoteApi()) {
    return apiRequest(`/posts/${postId}/comments`, {
      method: 'POST',
      token,
      body: { body, authorName },
    });
  }

  const posts = readLocalPosts().map((post) => {
    if (post.id !== postId) {
      return post;
    }

    return normalizePost({
      ...post,
      comments: [
        ...post.comments,
        {
          id: generateId(),
          body,
          authorName,
          createdAt: new Date().toISOString(),
        },
      ],
    });
  });

  writeLocalPosts(posts);
  return sortPosts(posts).find((post) => post.id === postId);
}

export async function reactToBlogPost(postId, { reaction, userKey, token }) {
  if (usesRemoteApi()) {
    return apiRequest(`/posts/${postId}/reactions`, {
      method: 'POST',
      token,
      body: { reaction, userKey },
    });
  }

  const oppositeReaction = reaction === 'like' ? 'dislikes' : 'likes';
  const targetReaction = reaction === 'like' ? 'likes' : 'dislikes';

  const posts = readLocalPosts().map((post) => {
    if (post.id !== postId) {
      return post;
    }

    const normalizedPost = normalizePost(post);
    const currentUsers = normalizedPost.reactionUsers[targetReaction];
    const alreadyReacted = currentUsers.includes(userKey);

    return {
      ...normalizedPost,
      reactionUsers: {
        [targetReaction]: alreadyReacted
          ? currentUsers.filter((currentUserKey) => currentUserKey !== userKey)
          : [...currentUsers, userKey],
        [oppositeReaction]: normalizedPost.reactionUsers[oppositeReaction].filter((currentUserKey) => currentUserKey !== userKey),
      },
    };
  });

  writeLocalPosts(posts);
  return sortPosts(posts).find((post) => post.id === postId);
}

export async function updateBlogPost(postId, { title, content, tags, token }) {
  if (usesRemoteApi()) {
    return apiRequest(`/posts/${postId}`, {
      method: 'PUT',
      token,
      body: { title, content, tags },
    });
  }

  const posts = readLocalPosts().map((post) => {
    if (post.id !== postId) return post;

    return normalizePost({
      ...post,
      title: typeof title === 'string' ? title : post.title,
      content: typeof content === 'string' ? content : post.content,
      tags: typeof tags === 'string' ? tags : post.tags || '',
    });
  });

  writeLocalPosts(posts);
  return sortPosts(posts).find((p) => p.id === postId);
}